import { Chip } from '@fe-learner-common';
import { AnimatePresence, motion } from 'framer-motion';
import { FC } from 'react';
import { useIntl } from 'react-intl';

export type FilterChip = {
  value: string;
  label: string;
  group: {
    key: string;
    label: string;
  };
};
type FilterChipsProps = {
  chips?: FilterChip[];
  nrOfResults: number;
  onRemoveFilter: (groupKey: string, filterKey: string) => void;
};

const transition = { duration: 0.2, ease: 'easeOut' } as const;

export const FilterChips: FC<FilterChipsProps> = ({
  chips = [],
  nrOfResults,
  onRemoveFilter,
}) => {
  const intl = useIntl();

  const handleRemove = (groupKey: string, filterKey: string) => {
    onRemoveFilter(groupKey, filterKey);
  };

  return (
    <AnimatePresence initial={false}>
      {chips.length > 0 && (
        <motion.div
          className="relative z-0 flex items-start gap-3"
          initial={{ opacity: 0, height: 0, marginBottom: 0 }}
          animate={{ opacity: 1, height: 'auto', marginBottom: 20 }}
          exit={{ opacity: 0, height: 0, marginBottom: 0 }}
          transition={transition}
        >
          <p className="sr-only" aria-atomic aria-live="polite">
            {intl.formatMessage(
              { id: 'learner.common.filter.sr.filtersApplied' },
              {
                nr: chips.length,
              },
            )}
            {intl.formatMessage(
              { id: 'learner.common.filter.sr.results' },
              {
                nr: nrOfResults,
              },
            )}
          </p>

          <ul className="flex flex-wrap gap-2">
            <AnimatePresence initial={false} mode="popLayout">
              {chips.map((chip) => {
                const isStable = chip.group.key === 'search';
                return (
                  <motion.li
                    key={
                      isStable
                        ? `chip-${chip.group.key}`
                        : `chip-${chip.group.key}-${chip.value}`
                    }
                    layout={!isStable}
                    className="whitespace-nowrap"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={transition}
                  >
                    <Chip
                      aria-describedby="remove-filter-description"
                      label={`${chip.group.label}: ${chip.label}`}
                      className="first-letter:capitalize"
                      onRemove={() => handleRemove(chip.group.key, chip.value)}
                    />
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
